import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Search, Megaphone, TrendingUp, BarChart3, Building, Globe2, Compass, Handshake, Brain, MapPin, Target } from "lucide-react";
import { MediaSection } from "@/components/site/MediaSection";
import { SAME_AS } from "@/data/media";
import { Seo, breadcrumbLd } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { SectionHead } from "@/components/site/SectionHead";
import { Testimonials } from "@/components/site/Testimonials";
import { CtaBand } from "@/components/site/CtaBand";
import { useSettings, useContent, SITE_URL } from "@/lib/api";

const EXPERTISE = [[Search, "PPC & Google Ads"], [Megaphone, "Meta Ads"], [TrendingUp, "SEO & GEO"], [BarChart3, "CRO & analytics"]];
const APPROACH = [
  [Brain, "Psychology & behavioural science", "Training in psychology, NLP and behavioural science shapes how I plan campaigns, write ads and design user journeys around how people really decide."],
  [MapPin, "Based in Tunbridge Wells, Kent", "Digital AdDoctor is based in Tunbridge Wells and works with businesses across Kent, as well as clients elsewhere in the UK and internationally."],
  [Target, "PPC, SEO and CRO together", "Paid search, organic search and conversion rate optimisation work best as one joined-up plan, so every visit has a better chance of becoming a customer."],
];

const Approach = () => (
  <section className="bg-white py-24 sm:py-28" data-testid="about-approach">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="My approach" title={<>Where marketing meets <span className="serif-accent font-normal text-[#581C87]">psychology.</span></>}
        text="Great digital marketing starts with understanding people. After more than 18 years in digital marketing, that belief still guides everything I do." />
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {APPROACH.map(([I, t, d], i) => (
          <Reveal key={t} delay={i * 0.06} className="rounded-[24px] bg-[#F5F0FF] p-7">
            <I className="h-6 w-6 text-[#7C3AED]" />
            <h3 className="mt-4 text-lg font-extrabold text-[#13052A]">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
const POINTS = [
  [Building, "Small businesses to larger organisations", "Experience across very different budgets, teams and goals."],
  [Globe2, "UK & international campaigns", "Including clients in the UK and the Middle East."],
  [Compass, "Strategic & analytical", "Commercially focused, with decisions grounded in data and margins."],
  [Handshake, "Direct, senior support", "You work with an experienced consultant, not a junior team."],
];

export default function About() {
  const s = useSettings();
  const { items: testimonials } = useContent("testimonials");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const crumbs = [["Home", "/"], ["About Freya Jones", "/about-freya-jones"]];
  const story = String(s.about_story || "").split(/\n{2,}/).filter(Boolean);

  return (
    <>
      <Seo title="Freya Jones | Founder of Digital AdDoctor, Tunbridge Wells" path="/about-freya-jones" type="profile"
        description="Freya Jones, founder of Digital AdDoctor in Tunbridge Wells, Kent: 18+ years in PPC, SEO and CRO, with a background in psychology and behavioural science."
        jsonLd={[{ "@type": "ProfilePage", url: `${SITE_URL}/about-freya-jones`, mainEntity: { "@type": "Person", "@id": `${SITE_URL}/#freya`, name: "Freya Jones", jobTitle: "Founder & Digital Marketing Consultant", image: `${SITE_URL}/img/freya-41.jpg`,
          url: `${SITE_URL}/about-freya-jones`, worksFor: { "@id": `${SITE_URL}/#business` }, workLocation: { "@type": "Place", name: "Tunbridge Wells, Kent" }, sameAs: SAME_AS,
          knowsAbout: ["Pay-per-click advertising", "Google Ads", "Meta Ads", "Search Engine Optimisation", "Conversion Rate Optimisation", "Google Analytics 4", "Behavioural science"] } }, breadcrumbLd(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="About Freya Jones · Tunbridge Wells, Kent" lines={["Hi, I'm Freya Jones.", <>Let&rsquo;s grow <span className="serif-accent font-normal text-[#C4B5FD]">together.</span></>]}
        intro={s.about_intro} />

      <section ref={ref} className="bg-white py-24 sm:py-32" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl items-start gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr]">
          <Reveal className="relative lg:sticky lg:top-28">
            <div className="relative mx-auto aspect-[3/4] max-w-md overflow-hidden rounded-[32px] bg-[#EDE9FE]">
              <motion.img style={{ y }} src="/img/freya-41.jpg" alt="Freya Jones sitting on a staircase in her studio" className="h-[112%] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHead eyebrow="My story" title={<>18+ years of making marketing <span className="serif-accent font-normal text-[#581C87]">make sense.</span></>} />
            <Reveal delay={0.1} className="mt-8 space-y-5 text-base leading-relaxed text-slate-600 md:text-lg">
              {story.map((p, i) => <p key={i}>{p}</p>)}
            </Reveal>
            <Reveal delay={0.15} className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {EXPERTISE.map(([I, t]) => (
                <div key={t} className="rounded-2xl bg-[#F5F0FF] p-4 text-center">
                  <I className="mx-auto h-5 w-5 text-[#7C3AED]" />
                  <p className="mt-2 text-sm font-bold text-[#13052A]">{t}</p>
                </div>
              ))}
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {POINTS.map(([I, t, d], i) => (
                <Reveal key={t} delay={i * 0.05} className="rounded-2xl border border-violet-200 p-5">
                  <I className="h-5 w-5 text-[#7C3AED]" />
                  <h3 className="mt-3 text-base font-extrabold text-[#13052A]">{t}</h3>
                  <p className="mt-1 text-sm text-slate-600">{d}</p>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact?type=call" className="btn-primary" data-testid="about-contact-cta">Talk to Freya <ArrowUpRight className="h-4 w-4" /></Link>
              <Link to="/results" className="btn-ghost" data-testid="about-results-link">See my work</Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#13052A] py-24 text-white sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <SectionHead dark eyebrow="Why a consultant, not an agency" title="The advantages of working directly with me"
            text="Agencies have their place. But many growing businesses get more from one experienced specialist who knows their account inside out, responds quickly and is accountable for the results." />
          <Reveal className="overflow-hidden rounded-[32px]">
            <img src="/img/freya-86.jpg" alt="Freya Jones with a coffee, relaxed in her studio" loading="lazy" className="aspect-[4/3] w-full object-cover object-[50%_20%]" />
          </Reveal>
        </div>
      </section>

      <Approach />
      <MediaSection />

      <section className="bg-[#F5F0FF] py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow="Testimonials" title="What people say" />
          <div className="mt-12"><Testimonials items={testimonials} /></div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
